/**
 * R3 and D2: the next game as a compact card directly under the header,
 * above the tab strip, visible without scrolling at 390px.
 *
 * Takes an already-mapped row model or null. Choosing WHICH game is next is
 * `nextTeamGame`'s job (`teamScheduleLogic.ts`); this component never
 * filters.
 *
 * A failed schedule read shows the failed notice, never the empty line
 * (ISSUE-1021).
 *
 * The kickoff label is handed in already resolved (ISSUE-1178); the card
 * never reads a clock. On a phone it sits under the opponent, so a long
 * school or club name wraps between words, never inside one.
 */
import { cn, FOCUS_RING } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { TeamMark } from "../gameDetail/TeamMark";
import type { typeTeamGameRowModel } from "./teamGameLogic";
import { TEAM_SCHEDULE_FAILED_LINE } from "./teamProfileLogic";
import { TeamReadFailed } from "./TeamReadFailed";

export const TEAM_NEXT_GAME_HEADING = "Next game";
export const TEAM_NEXT_GAME_EMPTY = "No game scheduled yet.";

export type typeTeamNextGameCardProps = {
  row: typeTeamGameRowModel | null;
  /** What the card shows for the kickoff: resolveKickoffLabel's output (ISSUE-1178). "" when row is null. */
  kickoffLabel: string;
  onOpenGame: (eventId: string) => void;
  /** True when the schedule read failed. Wins over the row and the empty line. */
  isFailed: boolean;
  onRetry: () => void;
};

export const TeamNextGameCard = (
  props: typeTeamNextGameCardProps,
): ReactNode => (
  <section
    data-testid="team-next-game"
    className="rounded-md border border-hairline bg-surface"
  >
    <p className="px-4 pt-3 text-caption text-ink-secondary">
      {TEAM_NEXT_GAME_HEADING}
    </p>
    {props.isFailed ? (
      <TeamReadFailed
        layout="panel"
        testId="team-next-game-failed"
        line={TEAM_SCHEDULE_FAILED_LINE}
        onRetry={props.onRetry}
      />
    ) : props.row === null ? (
      <p
        data-testid="team-next-game-empty"
        className="px-4 pb-3 text-body text-ink-secondary"
      >
        {TEAM_NEXT_GAME_EMPTY}
      </p>
    ) : (
      (() => {
        const row = props.row;
        return (
          <button
            type="button"
            data-testid="team-next-game-row"
            onClick={() => {
              props.onOpenGame(row.eventId);
            }}
            aria-label={`${TEAM_NEXT_GAME_HEADING}, ${row.opponentLine}, ${props.kickoffLabel}`}
            className={cn(
              "flex w-full min-h-touch-target items-center gap-3 px-4 py-3 text-left",
              FOCUS_RING,
            )}
          >
            <TeamMark
              name={row.opponentLine}
              abbreviation={row.opponentAbbreviation}
              logoUrl={row.opponentLogoUrl}
              size="md"
            />
            {/* Under 640px the kickoff goes under the opponent: beside it,
                the fixed kickoff left the name about 40px and broke
                "Cincinnati Bengals" mid-word (QA round 1). */}
            <span
              data-testid="team-next-game-text"
              className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-3"
            >
              <span className="min-w-0 text-body-strong text-ink sm:flex-1">
                {row.opponentLine}
              </span>
              <span className="text-body text-ink-secondary tabular-nums sm:ml-auto sm:shrink-0 sm:text-body-strong sm:text-ink">
                {props.kickoffLabel}
              </span>
            </span>
          </button>
        );
      })()
    )}
  </section>
);
